import { withApollo } from "@apollo/client/react/hoc";
import {
  find,
  findKey,
  flatten,
  includes,
  isEqual,
  isUndefined,
  keys,
  map,
  omit,
} from "lodash";
import PropTypes from "prop-types";
import React from "react";

// @material-ui/core components
import { withStyles } from "@mui/styles";

// import Grid from "@material-ui/core/Grid";
// core components
import {
  Button,
  Card,
  CardBody,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import generalizedAnxietyFollowUp from "~medical/generalizedAnxietyFollowUp.js";
import insomniaFollowUpTrial from "~medical/insomnia-follow-up-trial.js";
import insomniaInitial from "~medical/insomnia-initial.js";
import performanceAnxietyFollowUp from "~medical/performanceAnxietyFollowUp.js";
import performanceAnxietyInitial from "~medical/performanceAnxietyInitial.js";
import scopolamineAddon from "~medical/scopolamine-addon.js";

import ConsultationQuestion from "./consultationQuestion.jsx";
import VisitProgress from "./visitProgress.jsx";

import { bugsnagClient } from "~/shared/utils/errorHandling.js";
import KickClient from "../utils/api.js";

import Cookies from "js-cookie";
import {
  getConsultAnswersJSON,
  getForceFlavoredBBs,
  getUpgradingTo2Pack,
  getUserID,
  getVisitEmail,
  getVisitGender,
  getVisitID,
  getVisitPhone,
  getVisitProduct,
  getVisitUSState,
  setActiveSubscriptionBySubType,
  setConsultAnswersJSON,
  setFollowUpVisitFlag,
  setIsPickup,
  setVisitID,
  setVisitProduct,
} from "../utils/visitCookies.js";

import { kickRed } from "~/shared/styles/baseStyles.js";

import {
  COLOR_KICK_BLUE,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON,
  DB_STATUS_ACTIVE,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
} from "~/shared/sharedConstants.js";

import {
  getConsultationTypeByProduct,
  isConsultationTypeInsomniaFollowUp,
  isConsultationTypeInsomniaInitial,
  isDevelopmentEnvironment,
  isFollowUpConsultationType,
  isPharmacyPickupOrderType,
} from "~/shared/utils/sharedUtils.js";

import {
  checkAndRedirectToFollowUpIfNeeded,
  ensureCheckoutSessionID,
} from "../utils/visitUtils.js";

const addonQuestionnaires = [CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON];

function getCurrentQuestionID({ consultAnswers, questionId }) {
  if (!consultAnswers) {
    return 1;
  }
  const questionIndexes = Object.keys(consultAnswers)
    .map(Number)
    .sort((a, b) => a - b);
  let currentQuestionIndex = questionId - 1;

  if (!questionIndexes.length) {
    return 1;
  }

  if (questionIndexes[questionIndexes.length - 1] + 1 >= currentQuestionIndex) {
    // questionId is less than or equal to the last unanswered question index
    // go to questionId
    return questionId;
  } else if (
    currentQuestionIndex >
    questionIndexes[questionIndexes.length - 1] + 1
  ) {
    // questionId is greater than the last answered question.
    // Go to next unanswered question.
    return questionIndexes[questionIndexes.length - 1] + 2;
  }
}

class Consultation extends React.Component {
  constructor(props) {
    super(props);
    const consultationType = this.props.match.params.consultationType;
    const queryParams = new URLSearchParams(this.props.location.search);
    const checkoutSessionID = queryParams.get("checkoutSessionID");

    this.state = {
      consultationType,
      checkoutSessionID,
      questionId: this.props.match.params.questionId,
      questionError: "",
      refresh: false,
      isAddon: addonQuestionnaires.includes(consultationType),
    };

    // rehydrate from local storage for refreshes
    const consultAnswers = (getConsultAnswersJSON() || {})[
      `${consultationType}-${this.consultIntakeQuestionnaire().version}`
    ];

    this.state.consultAnswers = consultAnswers || {};

    this.state.questionIndex = Number(this.state.questionId) - 1;

    // enforce question index in development environment
    if (!isDevelopmentEnvironment()) {
      const currentQID = getCurrentQuestionID(this.state);
      this.state.questionIndex = currentQID - 1;
      if (this.state.questionId !== currentQID) {
        this.props.history.push(
          `/visits/${this.state.consultationType}/${currentQID}?checkoutSessionID=${this.state.checkoutSessionID}`,
        );
      }
    }

    ensureCheckoutSessionID(checkoutSessionID, this.props.history);
  }

  validateCookies() {
    const consultationType = this.props.match.params.consultationType;
    // No need to validate for follow-up consultations. We already have the data.
    if (isFollowUpConsultationType(consultationType)) {
      return;
    }
    if (!getUserID() || !getVisitEmail() || !getVisitUSState()) {
      return this.props.history.push(`/visits/${consultationType}/signup`);
    }
    if (!getVisitGender() || !getVisitPhone()) {
      return this.props.history.push(
        `/visits/${consultationType}/signup-details`,
      );
    }
  }

  async componentDidMount() {
    this.validateCookies();

    // Check for existing completed visits and redirect to follow-up if needed
    await checkAndRedirectToFollowUpIfNeeded(
      this.props.client,
      this.state.consultationType,
      this.state.checkoutSessionID,
    );

    // Set up navigation blocking for unsaved changes
    this.unblock = this.props.history.block(() => {
      // Check if we have unsaved changes
      const hasUnsavedChanges = this.checkForUnsavedChanges();

      if (hasUnsavedChanges) {
        // Show alert instead of browser prompt
        alert("Please click the 'Continue' button below to save your changes");
        // Return false to prevent navigation
        return false;
      }

      // Allow navigation if no unsaved changes
      return true;
    });
  }

  componentWillUnmount() {
    // Clean up the history blocking when component unmounts
    if (this.unblock) {
      this.unblock();
    }
  }

  componentDidUpdate(prevProps) {
    // sync questionId and questionIndex so child question component refreshes - fixes back bug
    const questionID = Number(this.props.match.params.questionId);
    const prevQuestionID = Number(prevProps.match.params.questionId);

    if (this.state.questionId != questionID) {
      // When the question changes after user confirmed navigation with unsaved changes
      if (
        prevQuestionID !== questionID &&
        this.unsavedQuestionIndex !== undefined
      ) {
        // Reset the answer of the question we navigated away from
        this.resetAnswerForQuestion(this.unsavedQuestionIndex);
        this.unsavedQuestionIndex = undefined;
      }

      this.setState({
        questionId: questionID,
        questionIndex: questionID - 1,
      });
    }

    this.validateCookies();
  }

  checkForUnsavedChanges = () => {
    // Get saved answer for current question
    const savedAnswers = getConsultAnswersJSON();
    const versionKey = `${this.state.consultationType}-${
      this.consultIntakeQuestionnaire().version
    }`;
    const savedAnswer = savedAnswers?.[versionKey]?.[this.state.questionIndex];
    const currentAnswer = this.state.consultAnswers[this.state.questionIndex];

    if (currentAnswer?.type === "text") {
      return !isEqual(savedAnswer?.value, currentAnswer?.value);
    } else {
      // Exclude 'question' property from comparison since it's metadata
      // that can change (e.g., labels with HTML) without user interaction
      return !isEqual(
        omit(savedAnswer, "question"),
        omit(currentAnswer, "question"),
      );
    }
  };

  resetAnswerForQuestion = (questionIndex) => {
    // Get saved answer for the specified question
    const savedAnswers = getConsultAnswersJSON();
    const versionKey = `${this.state.consultationType}-${
      this.consultIntakeQuestionnaire().version
    }`;
    const savedAnswer = savedAnswers?.[versionKey]?.[questionIndex];

    // Reset the answer to saved state
    if (savedAnswer !== undefined) {
      this.setState((prevState) => ({
        consultAnswers: {
          ...prevState.consultAnswers,
          [questionIndex]: savedAnswer,
        },
      }));
    } else {
      // Clear the answer if no saved state
      this.setState((prevState) => {
        const newAnswers = { ...prevState.consultAnswers };
        delete newAnswers[questionIndex];
        return { consultAnswers: newAnswers };
      });
    }
  };

  totalQuestionsCount = () => {
    let totalCount = this.consultIntakeQuestionnaire().questions.length;
    return totalCount;
  };

  consultIntakeQuestionnaire = () => {
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
        return performanceAnxietyInitial();
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
        return performanceAnxietyFollowUp();

      case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
        // For now, use performance anxiety initial until GA initial questionnaire is created
        return performanceAnxietyInitial();
      case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
        return generalizedAnxietyFollowUp();

      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
        return insomniaInitial();
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        return insomniaFollowUpTrial(this.state.checkoutSessionID);
      case CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON:
        return scopolamineAddon();
    }

    return performanceAnxietyInitial;
  };

  isInitialVisit = () => {
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        return false;
    }
    return true;
  };

  validateAllQuestionsAnswered = () => {
    if (process.env.NODE_ENV === "development") {
      return -1;
    }
    const questions = this.consultIntakeQuestionnaire().questions;

    for (let i = 0; i < questions.length; i++) {
      if (this.state.consultAnswers[i] === undefined) {
        return i;
      }
    }

    return -1;
  };

  saveIntake = () => {
    const unansweredQuestionIdx = this.validateAllQuestionsAnswered();
    if (unansweredQuestionIdx !== -1) {
      this.props.history.push(
        `/visits/${this.state.consultationType}/${
          unansweredQuestionIdx + 1
        }?checkoutSessionID=${this.state.checkoutSessionID}`,
      );
      return;
    }

    const path = `/visits/questionnaire/${this.state.consultationType}/${
      this.consultIntakeQuestionnaire().version
    }/${getVisitID(this.state.checkoutSessionID) || ""}`;

    KickClient.newAuthenticatedClient()
      .post(path, this.state.consultAnswers)
      .then((response) => {
        // store visit ID
        const { visit, subscriptions } = response.data;
        setVisitID(visit.id, this.state.checkoutSessionID);
        subscriptions?.forEach((sub) => {
          if (sub.attributes.status === DB_STATUS_ACTIVE) {
            setActiveSubscriptionBySubType(sub.attributes.subType, {
              id: sub.id,
              ...sub.attributes,
            });
          }
        });

        // Set cookies from response data for downstream pages
        if (visit?.attributes?.orderType) {
          setVisitProduct(
            visit.attributes.orderType,
            this.state.checkoutSessionID,
          );
        }
        if (isConsultationTypeInsomniaFollowUp(this.state.consultationType)) {
          setFollowUpVisitFlag("yes", this.state.checkoutSessionID);
          const orderType = visit?.attributes?.orderType || "";
          if (isPharmacyPickupOrderType(orderType)) {
            setIsPickup(true);
          }
        }

        if (getForceFlavoredBBs(this.state.checkoutSessionID)) {
          return this.props.history.push(
            `/visits/${this.state.consultationType}/select-num-packs-flavored?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }

        // upgrading to 2 pack --> go straight to select flavored
        if (
          getUpgradingTo2Pack(this.state.checkoutSessionID) &&
          getVisitProduct(this.state.checkoutSessionID) ===
            ORDER_TYPE_PROP_2_PACK_FOLLOW_UP
        ) {
          return this.props.history.push(
            `/visits/${CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP}/select-flavored?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }

        if (isConsultationTypeInsomniaFollowUp(this.state.consultationType)) {
          // Derive routing from response data: check orderType and subscriptions
          const orderType = visit?.attributes?.orderType || "";
          const hasActiveSleepSub = subscriptions?.some(
            (sub) =>
              sub.attributes.subType === SUBSCRIPTION_SUB_TYPE_SLEEP &&
              sub.attributes.status === DB_STATUS_ACTIVE,
          );
          const shouldGoToPharmacy =
            !orderType ||
            isPharmacyPickupOrderType(orderType) ||
            !hasActiveSleepSub;

          if (shouldGoToPharmacy) {
            this.props.history.push(
              `/visits/${this.state.consultationType}/select-pharmacy?checkoutSessionID=${this.state.checkoutSessionID}`,
            );
          } else {
            this.props.history.push(
              `/visits/${this.state.consultationType}/confirm-shipping-details?checkoutSessionID=${this.state.checkoutSessionID}`,
            );
          }
        } else if (
          // initial insomnia visit, go to membership description
          isConsultationTypeInsomniaInitial(this.state.consultationType)
        ) {
          this.props.history.push(
            `/visits/${this.state.consultationType}/sleep-membership-description?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else if (this.state.isAddon) {
          // scopolamine visit, go to payment
          const mainConsultationType = getConsultationTypeByProduct(
            getVisitProduct(this.state.checkoutSessionID),
          ); // get original consultation type
          this.props.history.push(
            `/visits/${mainConsultationType}/payment/order-details?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else if (
          this.state.consultationType ===
          CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP
        ) {
          this.props.history.push(
            `/visits/${this.state.consultationType}/select-ga-product?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else {
          // performance anxiety visit, go to select pharmacy
          this.props.history.push(
            `/visits/${this.state.consultationType}/select-pharmacy?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }
      })
      .catch((error) => {
        bugsnagClient.user = {
          email: getVisitEmail(),
        };

        bugsnagClient.notify(error, {
          beforeSend: function (report) {
            report.metaData = {
              cookies: Cookies.get(),
              localStorage: localStorage,
              visit: { email: getVisitEmail() },
            };
          },
        });

        // at this point, if code is 401, we need to redirect to login and then back to the questionnaire at the current question
        if (error?.response?.status === 401) {
          window.location.href = `/me/login?redirect=/visits/${this.state.consultationType}/${this.state.questionId}?checkoutSessionID=${this.state.checkoutSessionID}`;
        } else {
          this.setState({
            questionError: (
              <>
                There was an error saving your intake. Please try again or
                please retry filling out the intake by{" "}
                <a
                  style={{ color: "white", fontWeight: "bold" }}
                  href={`${window.location.origin}/visits/${this.state.consultationType}/1?checkoutSessionID=${this.state.checkoutSessionID}`}
                >
                  clicking here
                </a>
                .
              </>
            ),
          });
          console.log("Error saving intake: ", error);
        }
      });
  };

  nextQuestion = () => {
    this.setState({ questionError: "" });
    // store answers in local storage
    const allAnswers = getConsultAnswersJSON();
    setConsultAnswersJSON({
      ...allAnswers,
      [`${this.state.consultationType}-${
        this.consultIntakeQuestionnaire().version
      }`]: this.state.consultAnswers,
    });

    console.log("getConsultAnswersJSON()", getConsultAnswersJSON());
    console.log("visitID", getVisitID(this.state.checkoutSessionID));
    console.log("this.state.consultationType", this.state.consultationType);

    if (
      this.state.questionId >=
      this.consultIntakeQuestionnaire().questions.length
    ) {
      // persist to db
      this.saveIntake();
    } else {
      // Just go to next question...
      let newQuestionId = Number(this.state.questionId) + 1;
      this.setState({
        questionId: newQuestionId,
        questionIndex: newQuestionId - 1,
        questionError: "",
      });

      this.props.history.push(
        `/visits/${this.state.consultationType}/${newQuestionId}?checkoutSessionID=${this.state.checkoutSessionID}`,
      );

      window.scrollTo(0, 100);
    }
  };

  validateQuestionAnswers(question, answer) {
    const topLevelValid = this.validateAnswerCompleted(
      question.type,
      answer,
      question.minLength,
    );

    if (!topLevelValid) {
      this.setState({ questionError: "Please answer the question" });
      return false;
    }

    if (!find(question.choices, "children")) return true;

    switch (question.type) {
      case "checkbox": {
        var checkedAnswers = omit(answer, "question");

        const arrValid = map(keys(checkedAnswers), (key) => {
          if (checkedAnswers[key].checked == false) return null;
          if (!question.choices[key].children) return null;

          // childAnswers should be defined if there's been any activity on child
          if (!checkedAnswers[key].childAnswers) {
            this.setState({
              questionError: "Please answer all the follow-on questions",
            });
            return false;
          }

          return map(checkedAnswers[key].childAnswers, (subQ) => {
            return this.validateAnswerCompleted(subQ.type, subQ);
          });
        });

        if (includes(flatten(arrValid), false)) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }
        break;
      }
      case "radio": {
        // no child questions, we're done since top level answer is valid
        if (isUndefined(question.choices[answer.selectedIndex].children))
          return true;

        const childQuestions = question.choices[answer.selectedIndex].children;
        // none of child questions have been answered - invalid so return false
        if (isUndefined(answer[answer.selectedIndex])) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }

        const childAnswers = answer[answer.selectedIndex].childAnswers;
        // if (isUndefined(childAnswers)) {
        //   this.setState({
        //     questionError: "Please answer all the follow-on questions"
        //   });
        //   return false;
        // }

        const arrRadioValid = map(childQuestions, (childQ) => {
          // childAnswers should be defined if there's been any activity on child
          if (isUndefined(childAnswers[childQ.code])) {
            console.error("QCode not found in answer");
            return false;
          }

          // Validate child answer itself
          const childValid = map(childAnswers, (subQ) => {
            return this.validateAnswerCompleted(subQ.type, subQ);
          });

          // Check for grandchildren (3rd level) if child is a radio question
          if (childQ.type === "radio") {
            const childAnswer = childAnswers[childQ.code];
            if (childAnswer?.selectedIndex !== undefined) {
              const selectedChildChoice =
                childQ.choices[childAnswer.selectedIndex];

              // If selected choice has children (grandchildren), validate them
              if (selectedChildChoice?.children) {
                const grandchildQuestions = selectedChildChoice.children;
                const grandchildAnswers =
                  childAnswer[childAnswer.selectedIndex]?.childAnswers;

                if (isUndefined(grandchildAnswers)) {
                  this.setState({
                    questionError: "Please answer all the follow-on questions",
                  });
                  return false;
                }

                const grandchildValid = map(
                  grandchildQuestions,
                  (grandchildQ) => {
                    if (isUndefined(grandchildAnswers[grandchildQ.code])) {
                      console.error("Grandchild QCode not found in answer");
                      return false;
                    }

                    return this.validateAnswerCompleted(
                      grandchildQ.type,
                      grandchildAnswers[grandchildQ.code],
                    );
                  },
                );

                if (includes(grandchildValid, false)) {
                  this.setState({
                    questionError: "Please answer all the follow-on questions",
                  });
                  return false;
                }
              }
            }
          }

          return childValid;
        });

        if (includes(flatten(arrRadioValid), false)) {
          this.setState({
            questionError: "Please answer all the follow-on questions",
          });
          return false;
        }
        break;
      }
      case "textarea": {
        if (answer.value.length < question.minLength) {
          this.setState({
            questionError: "Please answer the question",
          });
          return false;
        }
        break;
      }
    }
    return true;
  }

  validateAnswerCompleted(questionType, answer, minLength = 1) {
    switch (questionType) {
      case "checkbox": {
        const checkedBox = findKey(answer, (o) => {
          return o.checked == true;
        });

        return !isUndefined(checkedBox);
      }
      case "radio":
      case "bloodpressure-systolic":
      case "bloodpressure-diastolic":
        return !isUndefined(answer.selectedIndex);

      case "text":
      case "textarea": {
        const trimmedValue = answer.value?.replace(/[^a-zA-Z0-9]/g, "");
        const isValid = trimmedValue.length >= minLength;
        return isValid;
      }
    }
  }

  recordAnswer = (questionIndex, answer) => {
    console.log(
      "recordAnswer",
      questionIndex,
      answer,
      this.state.consultAnswers,
    );
    console.log("visitProduct", getVisitProduct(this.state.checkoutSessionID));
    const newConsultAnswers = {
      ...this.state.consultAnswers,
      [questionIndex]: answer,
    };
    this.setState({
      consultAnswers: newConsultAnswers,
      questionError: "",
      refresh: !this.state.refresh,
    });
  };

  render() {
    const { classes, location } = this.props;

    return (
      <React.Fragment>
        <VisitProgress
          questionNumber={this.state.questionId}
          totalQuestions={this.totalQuestionsCount()}
        />
        <div
          className={classes.container}
          style={{
            backgroundColor: COLOR_KICK_BLUE,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Card
            className={classes.cardConsultation}
            style={{
              backgroundColor: COLOR_KICK_BLUE,
              boxShadow: "none",
              maxWidth: "900px",
              padding: "30px 0",
            }}
          >
            <CardBody style={{ border: "" }}>
              <GridContainer justifyContent="center">
                <GridItem xs={12} sm={12} md={8} noPadding>
                  <ConsultationQuestion
                    location={location}
                    questionIndex={this.state.questionIndex}
                    questionError={this.state.questionError}
                    question={
                      this.consultIntakeQuestionnaire().questions[
                        this.state.questionIndex
                      ]
                    }
                    consultAnswers={this.state.consultAnswers}
                    refresh={this.state.refresh}
                    recordAnswer={this.recordAnswer}
                  />

                  <div style={{ color: "red", margin: "20px auto 6px auto" }}>
                    <p style={{ textAlign: "center" }}>
                      {this.state.questionError}
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // question never answered
                      if (
                        this.state.consultAnswers[this.state.questionIndex] ==
                        null
                      ) {
                        this.setState({
                          questionError: "Please answer the question",
                        });
                        return;
                      }

                      var validAnswers = this.validateQuestionAnswers(
                        this.consultIntakeQuestionnaire().questions[
                          this.state.questionIndex
                        ],
                        this.state.consultAnswers[this.state.questionIndex],
                      );

                      if (!validAnswers) {
                        return;
                      }

                      this.nextQuestion();
                    }}
                    style={{
                      display: "block",

                      backgroundColor: kickRed,
                      margin: "0px auto",
                      width: "300px",
                      fontSize: "18px",
                      fontWeight: "medium",
                      textTransform: "unset",
                    }}
                  >
                    Continue
                  </Button>
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </div>
      </React.Fragment>
    );
  }
}

Consultation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withApollo(withStyles(consultationStyles)(Consultation));
